import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';

// Keep the visibility rule in sync with WhatsApp's compact positioning.
const compactControlsQuery = 'not ((width > 768px) and (height > 700px))';
const subscribeToCompactControls = (onChange: () => void) => {
  const mediaQuery = window.matchMedia(compactControlsQuery);
  mediaQuery.addEventListener('change', onChange);
  return () => mediaQuery.removeEventListener('change', onChange);
};

type LayoutBottomLeftContextValue = {
  isWhatsAppButtonVisible: boolean;
  isScrollIncentiveEnabled: boolean;
  isScrollIncentiveVisible: boolean;
  isWhatsAppButtonEnabled: boolean;
  setScrollIncentiveVisible: (visible: boolean) => void;
};

type LayoutBottomLeftProviderProps = {
  children: React.ReactNode;
  route: string;
  scrollIncentiveRoutes?: string[];
  whatsAppButtonRoutes?: string[];
};

type ScrollIncentiveStatus = 'pending' | 'visible' | 'dismissed';

type ScrollIncentiveState = {
  route: string;
  status: ScrollIncentiveStatus;
};

const fallbackContext: LayoutBottomLeftContextValue = {
  isWhatsAppButtonVisible: true,
  isScrollIncentiveEnabled: true,
  isScrollIncentiveVisible: false,
  isWhatsAppButtonEnabled: true,
  setScrollIncentiveVisible: () => undefined,
};

const LayoutBottomLeftContext =
  createContext<LayoutBottomLeftContextValue>(fallbackContext);

export const LayoutBottomLeftProvider = ({
  children,
  route,
  scrollIncentiveRoutes = [],
  whatsAppButtonRoutes = [],
}: LayoutBottomLeftProviderProps) => {
  const [scrollIncentiveState, setScrollIncentiveState] =
    useState<ScrollIncentiveState>({ route, status: 'pending' });

  const isCompactViewport = useSyncExternalStore(
    subscribeToCompactControls,
    () => window.matchMedia(compactControlsQuery).matches,
    () => false,
  );
  const isScrollIncentiveEnabled = scrollIncentiveRoutes.includes(route);
  const isWhatsAppButtonEnabled = whatsAppButtonRoutes.includes(route);
  const scrollIncentiveStatus: ScrollIncentiveStatus =
    scrollIncentiveState.route === route
      ? scrollIncentiveState.status
      : 'pending';
  const isScrollIncentiveVisible =
    isScrollIncentiveEnabled && scrollIncentiveStatus === 'visible';
  const isScrollIncentiveOccupyingSlot =
    isScrollIncentiveEnabled && scrollIncentiveStatus !== 'dismissed';

  const setScrollIncentiveVisible = useCallback((visible: boolean) => {
    const status: ScrollIncentiveStatus = visible ? 'visible' : 'dismissed';

    setScrollIncentiveState((current) => {
      // Dismissal lasts for this page visit, including the initial timer.
      if (
        current.route === route &&
        (current.status === 'dismissed' || current.status === status)
      ) {
        return current;
      }

      return { route, status };
    });
  }, [route]);

  const isWhatsAppButtonVisible =
    isWhatsAppButtonEnabled && (!isCompactViewport || !isScrollIncentiveOccupyingSlot);

  const value = useMemo(
    () => ({
      isWhatsAppButtonVisible,
      isScrollIncentiveEnabled,
      isScrollIncentiveVisible,
      isWhatsAppButtonEnabled,
      setScrollIncentiveVisible,
    }),
    [
      isWhatsAppButtonVisible,
      isScrollIncentiveEnabled,
      isScrollIncentiveVisible,
      isWhatsAppButtonEnabled,
      setScrollIncentiveVisible,
    ],
  );

  return (
    <LayoutBottomLeftContext.Provider value={value}>
      {children}
    </LayoutBottomLeftContext.Provider>
  );
};

export const useLayoutBottomLeftContext = () =>
  useContext(LayoutBottomLeftContext);
